// Path: sec-parser/client/src/pages/FilingsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, Table, Button, Tag, Typography, Space, Popconfirm,
  Empty, Badge, Tooltip, Collapse, Row, Col,
} from "antd";
import {
  RocketOutlined, DeleteOutlined, ReloadOutlined,
  TrophyOutlined, BarChartOutlined, GlobalOutlined, FileTextOutlined,
} from "@ant-design/icons";
import { getFilings, deleteFiling, getFilingRuns } from "../services/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const STATUS_COLORS = { uploaded: "blue", extracting: "orange", ready: "green", error: "red" };
const MODEL_COLORS  = { claude: "#8B5CF6", gpt4o: "#10A37F", gemini: "#4285F4", groq: "#F55036", grok: "#000", mistral: "#ff7000", deepseek: "#0066ff", cohere: "#D1B26F" };

export default function FilingsPage() {
  const navigate = useNavigate();
  const [filings, setFilings]     = useState([]);
  const [runs, setRuns]           = useState({});   // { filingId: [run, ...] }
  const [expanded, setExpanded]   = useState([]);
  const [loading, setLoading]     = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await getFilings();
    setFilings(data);
    setLoading(false);
    // Load benchmark runs for ready filings
    const ready = data.filter((f) => f.status === "ready");
    ready.forEach(async (f) => {
      try {
        const { data: runData } = await getFilingRuns(f._id);
        setRuns((prev) => ({ ...prev, [f._id]: runData }));
      } catch {}
    });
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    await deleteFiling(id);
    setFilings((prev) => prev.filter((f) => f._id !== id));
  };

  const columns = [
    {
      title: "Filing",
      key: "filing",
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Text strong style={{ fontSize: 14 }}>{r.companyName || r.originalName}</Text>
            {r.ticker && <Tag color="blue" style={{ fontSize: 11 }}>{r.ticker}</Tag>}
          </Space>
          <Space size={4}>
            {r.filingType && <Tag color="geekblue" style={{ fontSize: 10 }}>{r.filingType}</Tag>}
            <Tag
              color={r.source === "edgar-url" ? "green" : "orange"}
              icon={r.source === "edgar-url" ? <GlobalOutlined /> : <FileTextOutlined />}
              style={{ fontSize: 10 }}
            >
              {r.source === "edgar-url" ? "EDGAR" : "Upload"}
            </Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Best Score",
      key: "score",
      width: 110,
      render: (_, r) => {
        const filingRuns = runs[r._id] || [];
        const allResults = filingRuns.flatMap((run) => run.results || []);
        const best = allResults.reduce((b, res) =>
          (res.accuracyScore > (b?.accuracyScore ?? -1) ? res : b), null);
        if (!best) return <Text type="secondary">—</Text>;
        return (
          <Space>
            <TrophyOutlined style={{ color: "#faad14" }} />
            <Text strong style={{ color: "#1A3C5E" }}>{best.accuracyScore}%</Text>
            <Tag color={MODEL_COLORS[best.modelId] ? undefined : "default"}
              style={{ background: MODEL_COLORS[best.modelId], color: "#fff", border: "none", fontSize: 10 }}>
              {best.modelId?.toUpperCase()}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Runs",
      key: "runs",
      width: 80,
      render: (_, r) => {
        const count = (runs[r._id] || []).length;
        return count > 0
          ? <Badge count={count} style={{ background: "#1A3C5E" }} />
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: "Loaded",
      dataIndex: "createdAt",
      width: 130,
      render: (v) => (
        <Tooltip title={dayjs(v).format("MMM D, YYYY h:mm A")}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, r) => {
        const filingRuns = runs[r._id] || [];
        const latestRun  = filingRuns[0];
        return (
          <Space wrap>
            <Button
              size="small" type="primary" icon={<RocketOutlined />}
              disabled={r.status !== "ready"}
              onClick={() => navigate(`/benchmark/${r._id}`)}
              style={{ background: r.status === "ready" ? "#1A3C5E" : undefined }}
            >
              {latestRun ? "Re-run" : "Benchmark"}
            </Button>
            {latestRun && (
              <Button
                size="small" icon={<BarChartOutlined />}
                onClick={() => navigate(`/results/${latestRun._id}`)}
              >
                Results
              </Button>
            )}
            <Popconfirm title="Delete this filing?" onConfirm={() => handleDelete(r._id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Expandable row: show all benchmark runs with model-level scores
  const expandedRowRender = (record) => {
    const filingRuns = runs[record._id];
    if (!filingRuns) return <Text type="secondary">Loading runs…</Text>;
    if (filingRuns.length === 0) return <Text type="secondary">No benchmark runs yet.</Text>;

    return (
      <div style={{ padding: "8px 0" }}>
        {filingRuns.map((run) => (
          <Card
            key={run._id}
            size="small"
            style={{ marginBottom: 8 }}
            title={
              <Space>
                <Text style={{ fontSize: 12 }}>Run {run._id.slice(-6)}</Text>
                <Tag color={run.status === "complete" ? "green" : "orange"}>{run.status}</Tag>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(run.createdAt).fromNow()}
                </Text>
              </Space>
            }
            extra={
              <Button size="small" onClick={() => navigate(`/results/${run._id}`)}>
                View Results →
              </Button>
            }
          >
            <Row gutter={[8, 4]}>
              {(run.results || []).map((res) => (
                <Col key={res._id || res.modelId} xs={12} sm={8} md={6}>
                  <Space direction="vertical" size={2}
                    style={{ background: "#f5f7fa", borderRadius: 6, padding: "6px 10px", width: "100%" }}>
                    <Space>
                      <Tag
                        style={{ background: MODEL_COLORS[res.modelId] || "#999", color: "#fff", border: "none", fontSize: 10, margin: 0 }}>
                        {res.modelId?.toUpperCase()}
                      </Tag>
                      {run.winnerModelId === res.modelId && (
                        <TrophyOutlined style={{ color: "#faad14", fontSize: 12 }} />
                      )}
                    </Space>
                    <Text strong style={{ color: res.status === "error" ? "#f5222d" : "#1A3C5E", fontSize: 16 }}>
                      {res.status === "error" ? "Error" : `${res.accuracyScore ?? "?"}%`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {res.latencyMs ? `${(res.latencyMs / 1000).toFixed(1)}s` : "—"}
                    </Text>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} wrap>
        <Col>
          <Title level={3} style={{ color: "#1A3C5E", margin: 0 }}>My Filings</Title>
          <Text type="secondary">{filings.length} filings loaded</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={filings}
          rowKey="_id"
          loading={loading}
          columns={columns}
          scroll={{ x: 700 }}
          expandable={{
            expandedRowRender,
            expandedRowKeys: expanded,
            onExpand: (open, r) =>
              setExpanded((prev) => open ? [...prev, r._id] : prev.filter((id) => id !== r._id)),
            rowExpandable: (r) => r.status === "ready",
          }}
          locale={{ emptyText: <Empty description="No filings yet. Load one to get started." /> }}
        />
      </Card>
    </div>
  );
}